import { ContactList } from "@/components/contact-list";
import { DomainView } from "@/components/domain";
import { Badge } from "@/components/ui/badge";
import { useSheetContext } from "@/context/sheet";
import { useDeleteAgency, useGetAgency } from "@/hooks/agencies";
import { T } from "@/i18n";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Key } from "react";

export const Route = createFileRoute("/(agencies)/agencies/_sheet/$id/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data, isLoading } = useGetAgency(id);
  const { close } = useSheetContext();
  const navigate = useNavigate();
  const { mutate: deleteAgency } = useDeleteAgency({
    onSuccess: () => {
      close(() => {
        navigate({ to: "/agencies" });
      });
    },
  });

  return (
    <DomainView
      domain="agencies"
      editLink={(children, key) => (
        <Link to="/agencies/$id/edit" params={{ id }} key={key as Key}>
          {children}
        </Link>
      )}
      isLoading={isLoading}
      title={data?.name}
      onDelete={() => deleteAgency(id)}
      propsList={[
        {
          title: "agencies.fields.website",
          value: data?.website || "-",
        },
        {
          title: "agencies.fields.country",
          value: data?.country?.length ? (
            <T tKey={`countries.${data.country}`} />
          ) : (
            "-"
          ),
        },
        {
          title: "agencies.fields.tags",
          value: data?.tags?.length ? (
            <div className="flex items-center gap-1">
              {data.tags?.map((t) => <Badge key={t}>{t}</Badge>)}
            </div>
          ) : (
            "-"
          ),
        },
        {
          title: "agencies.fields.language",
          value: data?.language || "-",
        },
        {
          title: "agencies.fields.timeRestraintForDocumentation",
          value: data?.timeRestraintForDocumentation || "-",
        },
        {
          title: "agencies.fields.branding",
          value: <T tKey={`common.boolean.${!!data?.branding}`} />,
        },
        {
          title: "agencies.fields.digitalBook",
          value: data?.digitalBook ? (
            <T tKey={`agencies.digitalBook.${data.digitalBook}`}></T>
          ) : (
            "-"
          ),
        },
        {
          title: "agencies.fields.navigationApp",
          value: data?.navigationApp ? (
            <T tKey={`agencies.navigationApp.${data.navigationApp}`}></T>
          ) : (
            "-"
          ),
        },
        {
          title: "agencies.fields.tours",
          value: data?.tours?.length ? (
            <div className="flex items-center gap-1">
              {data.tours?.map((t) => (
                <Badge key={t}>
                  <T tKey={`agencies.toursEnum.${t}`} />
                </Badge>
              ))}
            </div>
          ) : (
            "-"
          ),
        },
        {
          title: "agencies.fields.guiding",
          value: data?.guiding?.length ? (
            <div className="flex items-center gap-1">
              {data.guiding?.map((g) => (
                <Badge key={g}>
                  <T tKey={`agencies.guiding.${g}`} />
                </Badge>
              ))}
            </div>
          ) : (
            "-"
          ),
        },
        {
          title: "agencies.fields.briefingLanguage",
          value: data?.briefingLanguage?.length ? (
            <div className="flex items-center gap-1">
              {data.briefingLanguage?.map((g) => <Badge key={g}>{g}</Badge>)}
            </div>
          ) : (
            "-"
          ),
        },
        {
          title: "agencies.fields.travefyRequired",
          value: <T tKey={`common.boolean.${!!data?.travefyRequired}`} />,
        },
        {
          title: "agencies.fields.rwgpsrequired",
          value: <T tKey={`common.boolean.${!!data?.rwgpsrequired}`} />,
        },
        {
          title: "agencies.fields.notes",
          value: data?.notes ?? "-",
        },
        {
          title: "agencies.fields.details",
          value: data?.details ?? "-",
        },
        {
          title: "agencies.fields.paymentConditions",
          value: data?.paymentConditions ? (
            <T
              tKey={`agencies.paymentConditions.${data.paymentConditions}`}
            ></T>
          ) : (
            "-"
          ),
        },
        {
          title: "agencies.fields.payment",
          value: data?.payment ? (
            <T tKey={`agencies.payment.${data.payment}`}></T>
          ) : (
            "-"
          ),
        },
        {
          title: "agencies.fields.priceCommission",
          value: data?.priceCommission ?? "-",
        },
        {
          title: "agencies.fields.seasonSupplementCommission",
          value: data?.seasonSupplementCommission ?? "-",
        },
        {
          title: "agencies.fields.individualRoomSupplementCommission",
          value: data?.individualRoomSupplementCommission ?? "-",
        },
        {
          title: "agencies.fields.soloTravelerCommission",
          value: data?.soloTravelerCommission ?? "-",
        },
        {
          title: "agencies.fields.bikesCommissions",
          value: data?.bikesCommissions ?? "-",
        },
        {
          title: "agencies.fields.extraNightsCommissions",
          value: data?.extraNightsCommissions ?? "-",
        },
        {
          title: "agencies.fields.roomChangeCommissions",
          value: data?.roomChangeCommissions ?? "-",
        },
        {
          title: "agencies.fields.servicesCommissions",
          value: data?.servicesCommissions ?? "-",
        },
        {
          title: "agencies.fields.contacts",
          value: <ContactList list={data?.contacts ?? []} />,
        },
      ]}
    />
  );
}
